import React, { createContext, useContext, useState, useEffect } from 'react';
import { ArrowLeft, ArrowRight, Home, AlertCircle, CheckCircle, Info } from 'lucide-react';

/**
 * NAVIGATION MANAGER FOR LIKILIMBA MOBILE APP
 * ===========================================
 * 
 * Complete navigation system with:
 * - Preview/prototype mode support
 * - Accessibility compliance
 * - Data persistence during navigation
 * - Error handling and fallbacks
 * - Visual feedback and simulations
 */

// Navigation Context
const NavigationContext = createContext();

// Navigation states and flow definition
const NAVIGATION_FLOWS = {
  onboarding: {
    steps: ['welcome', 'role-selection', 'create-group', 'join-group', 'complete'],
    labels: {
      'welcome': 'Bienvenue',
      'role-selection': 'Choisir un rôle', 
      'create-group': 'Créer un groupe',
      'join-group': 'Rejoindre un groupe',
      'complete': 'Terminé'
    }
  },
  groupCreation: {
    steps: ['basic-info', 'settings', 'visibility', 'confirmation', 'complete'],
    labels: {
      'basic-info': 'Informations de base',
      'settings': 'Paramètres',
      'visibility': 'Visibilité',
      'confirmation': 'Confirmation',
      'complete': 'Groupe créé'
    }
  },
  joinRequest: {
    steps: ['search', 'select', 'request', 'waiting', 'complete'],
    labels: {
      'search': 'Recherche',
      'select': 'Sélection',
      'request': 'Demande',
      'waiting': 'En attente',
      'complete': 'Rejoint'
    }
  }
};

// Navigation Provider Component
export const NavigationProvider = ({ children, isPreviewMode = false }) => {
  // Navigation state
  const [currentFlow, setCurrentFlow] = useState(null);
  const [currentStep, setCurrentStep] = useState(0);
  const [navigationStack, setNavigationStack] = useState([]);
  const [formData, setFormData] = useState({}); // Persistent form data
  const [simulationMode, setSimulationMode] = useState(isPreviewMode);
  const [feedbackMessage, setFeedbackMessage] = useState('');
  
  // Language support
  const [language, setLanguage] = useState('fr');
  const labels = {
    fr: {
      back: 'Retour',
      next: 'Suivant', 
      continue: 'Continuer',
      finish: 'Terminer',
      cancel: 'Annuler',
      simulatedNavigation: 'Navigation simulée vers',
      destinationNotFound: 'Destination non trouvée',
      navigationError: 'Erreur de navigation',
      dataPreserved: 'Vos données sont conservées',
      previewModeActive: 'Mode preview actif - Navigation simulée'
    },
    en: {
      back: 'Back',
      next: 'Next',
      continue: 'Continue', 
      finish: 'Finish',
      cancel: 'Cancel',
      simulatedNavigation: 'Simulated navigation to',
      destinationNotFound: 'Destination not found',
      navigationError: 'Navigation error',
      dataPreserved: 'Your data is preserved',
      previewModeActive: 'Preview mode active - Simulated navigation'
    },
    sw: {
      back: 'Rudi',
      next: 'Ifuatayo',
      continue: 'Endelea',
      finish: 'Maliza', 
      cancel: 'Ghairi',
      simulatedNavigation: 'Uongozi wa mfano kwenda',
      destinationNotFound: 'Lengo halipo',
      navigationError: 'Hitilafu ya uongozi',
      dataPreserved: 'Data yako imehifadhiwa',
      previewModeActive: 'Modi ya uhakiki - Uongozi wa mfano'
    }
  };

  // Get current labels based on language
  const t = labels[language] || labels.fr;

  // Initialize navigation flow
  const startFlow = (flowName, initialData = {}) => {
    if (!NAVIGATION_FLOWS[flowName]) {
      showFeedback('error', `${t.navigationError}: Flow '${flowName}' ${t.destinationNotFound}`);
      return false;
    }

    setCurrentFlow(flowName);
    setCurrentStep(0);
    setNavigationStack([{ flow: flowName, step: 0, timestamp: Date.now() }]);
    setFormData(initialData);
    
    if (simulationMode) {
      showFeedback('info', `${t.previewModeActive} - ${flowName}`);
    }
    
    return true;
  };

  // Navigate forward
  const goNext = (newData = {}, customDestination = null) => {
    // Preserve form data
    const updatedData = { ...formData, ...newData };
    setFormData(updatedData);

    if (!currentFlow) {
      showFeedback('error', t.navigationError);
      return false;
    }

    const flow = NAVIGATION_FLOWS[currentFlow];
    const nextStep = customDestination !== null ? customDestination : currentStep + 1;
    
    // Check if destination exists
    if (nextStep >= flow.steps.length) {
      if (simulationMode) {
        showFeedback('success', `${t.simulatedNavigation} fin du parcours. ${t.dataPreserved}.`);
        return true;
      }
      
      showFeedback('info', 'Parcours terminé');
      return false;
    }

    // Update navigation state
    setCurrentStep(nextStep);
    setNavigationStack(prev => [
      ...prev,
      { flow: currentFlow, step: nextStep, timestamp: Date.now(), data: updatedData }
    ]);

    // Show feedback
    const nextStepLabel = flow.labels[flow.steps[nextStep]];
    if (simulationMode) {
      showFeedback('success', `${t.simulatedNavigation} "${nextStepLabel}". ${t.dataPreserved}.`);
    }

    return true;
  };

  // Navigate backward
  const goBack = (preserveData = true) => {
    if (navigationStack.length <= 1) {
      if (simulationMode) {
        showFeedback('info', `${t.simulatedNavigation} écran principal. ${t.dataPreserved}.`);
        return true;
      }
      
      showFeedback('info', 'Déjà au début');
      return false;
    }

    // Go back in stack
    const newStack = [...navigationStack];
    newStack.pop(); // Remove current
    const previousState = newStack[newStack.length - 1];
    
    setNavigationStack(newStack);
    setCurrentFlow(previousState.flow);
    setCurrentStep(previousState.step);
    
    // Restore or preserve data
    if (preserveData && previousState.data) {
      setFormData({ ...previousState.data, ...formData });
    }

    // Show feedback
    const flow = NAVIGATION_FLOWS[previousState.flow];
    const stepLabel = flow?.labels[flow.steps[previousState.step]];
    
    if (simulationMode) {
      showFeedback('success', `${t.simulatedNavigation} "${stepLabel}". ${t.dataPreserved}.`);
    }

    return true;
  };

  // Show feedback message
  const showFeedback = (type, message) => {
    setFeedbackMessage({ type, message, timestamp: Date.now() });
    
    // Auto-hide after delay
    setTimeout(() => {
      setFeedbackMessage('');
    }, 4000);
  };

  // Get current step info
  const getCurrentStepInfo = () => {
    if (!currentFlow) return null;
    
    const flow = NAVIGATION_FLOWS[currentFlow];
    const stepKey = flow.steps[currentStep];
    
    return {
      flow: currentFlow,
      step: currentStep,
      stepKey,
      label: flow.labels[stepKey],
      isFirst: currentStep === 0,
      isLast: currentStep === flow.steps.length - 1,
      totalSteps: flow.steps.length,
      progress: ((currentStep + 1) / flow.steps.length) * 100
    };
  };

  // Navigation context value
  const navigationValue = {
    // State
    currentFlow,
    currentStep,
    formData,
    simulationMode,
    language,
    feedbackMessage,
    
    // Actions
    startFlow,
    goNext,
    goBack,
    setFormData,
    setLanguage,
    getCurrentStepInfo,
    showFeedback,
    
    // Labels
    t,
    
    // Utils
    isPreviewMode: simulationMode
  };

  return (
    <NavigationContext.Provider value={navigationValue}>
      {children}
    </NavigationContext.Provider>
  );
};

// Hook to use navigation
export const useNavigation = () => {
  const context = useContext(NavigationContext);
  if (!context) {
    throw new Error('useNavigation must be used within NavigationProvider');
  }
  return context;
};

// Enhanced Back Button with full accessibility and preview support
export const SmartBackButton = ({ 
  onCustomBack, 
  disabled = false,
  variant = 'default',
  className = '',
  preserveData = true
}) => {
  const { goBack, t, simulationMode, showFeedback } = useNavigation();
  
  const handleClick = () => {
    if (disabled) return;
    
    if (onCustomBack) {
      const result = onCustomBack();
      if (result === false) return; // Custom handler blocks navigation
    }
    
    const success = goBack(preserveData);
    if (!success && !simulationMode) {
      showFeedback('error', 'Navigation impossible');
    }
  };

  const handleKeyDown = (e) => {
    if ((e.key === 'Enter' || e.key === ' ') && !disabled) {
      e.preventDefault();
      handleClick();
    }
  };

  const baseClasses = "flex items-center gap-2 font-medium transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2";
  const variantClasses = {
    default: "bg-gray-100 hover:bg-gray-200 text-gray-700 px-4 py-3 rounded-lg border border-gray-300",
    minimal: "text-blue-600 hover:text-blue-800 hover:bg-blue-50 px-3 py-2 rounded-md",
    floating: "bg-white shadow-lg hover:shadow-xl text-gray-700 px-4 py-3 rounded-full border border-gray-200"
  };

  return (
    <button
      type="button"
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      disabled={disabled}
      className={`${baseClasses} ${variantClasses[variant]} ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'} ${className}`}
      aria-label={`${t.back} - Revenir à l'étape précédente`}
      aria-describedby="smart-back-description"
      role="button"
      tabIndex={disabled ? -1 : 0}
    >
      <ArrowLeft className="h-5 w-5" aria-hidden="true" />
      <span className="font-medium">{t.back}</span>
      
      {simulationMode && (
        <div className="w-2 h-2 bg-orange-400 rounded-full animate-pulse" title="Mode simulation" />
      )}
      
      {/* Hidden description for screen readers */}
      <span id="smart-back-description" className="sr-only">
        Navigation vers l'étape précédente. Vos données seront préservées. 
        {simulationMode ? ' Mode preview - Navigation simulée.' : ''}
      </span>
    </button>
  );
};

// Enhanced Next/Continue Button with preview support
export const SmartNextButton = ({ 
  onCustomNext,
  disabled = false,
  label,
  variant = 'primary',
  isLoading = false,
  className = '',
  newData = {}
}) => {
  const { goNext, t, getCurrentStepInfo, simulationMode, showFeedback } = useNavigation();
  
  const stepInfo = getCurrentStepInfo();
  const buttonLabel = label || (stepInfo?.isLast ? t.finish : t.next);
  
  const handleClick = () => {
    if (disabled || isLoading) return;
    
    if (onCustomNext) {
      const result = onCustomNext(newData);
      if (result === false) return; // Custom handler blocks navigation
      
      // If custom handler returns data, use it
      if (typeof result === 'object') {
        goNext(result);
        return;
      }
    }
    
    const success = goNext(newData);
    if (!success && !simulationMode) {
      showFeedback('error', 'Navigation impossible');
    }
  };

  const handleKeyDown = (e) => {
    if ((e.key === 'Enter' || e.key === ' ') && !disabled && !isLoading) {
      e.preventDefault();
      handleClick();
    }
  };

  const baseClasses = "flex items-center gap-2 font-medium transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2";
  const variantClasses = {
    primary: "bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-lg focus:ring-blue-500",
    success: "bg-green-600 hover:bg-green-700 text-white px-6 py-3 rounded-lg focus:ring-green-500",
    warning: "bg-orange-600 hover:bg-orange-700 text-white px-6 py-3 rounded-lg focus:ring-orange-500"
  };

  return (
    <button
      type="button"
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      disabled={disabled || isLoading}
      className={`${baseClasses} ${variantClasses[variant]} ${disabled || isLoading ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'} ${className}`}
      aria-label={`${buttonLabel} - ${stepInfo?.isLast ? 'Terminer le processus' : 'Continuer vers l\'étape suivante'}`}
      aria-describedby="smart-next-description"
      aria-busy={isLoading}
      role="button"
      tabIndex={disabled || isLoading ? -1 : 0}
    >
      <span>{buttonLabel}</span>
      
      {isLoading ? (
        <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" aria-hidden="true" />
      ) : (
        <ArrowRight className="h-5 w-5" aria-hidden="true" />
      )}
      
      {simulationMode && (
        <div className="w-2 h-2 bg-orange-400 rounded-full animate-pulse" title="Mode simulation" />
      )}
      
      {/* Hidden description for screen readers */}
      <span id="smart-next-description" className="sr-only">
        {stepInfo?.isLast ? 'Terminer et finaliser le processus' : 'Continuer vers l\'étape suivante'}. 
        Vos données seront automatiquement sauvegardées.
        {simulationMode ? ' Mode preview - Navigation simulée.' : ''}
      </span>
    </button>
  );
};

// Progress Indicator with accessibility
export const NavigationProgress = ({ className = '' }) => {
  const { getCurrentStepInfo, t } = useNavigation();
  const stepInfo = getCurrentStepInfo();
  
  if (!stepInfo) return null;
  
  return (
    <div className={`bg-white rounded-lg p-4 border border-gray-200 ${className}`}>
      {/* Progress bar */}
      <div className="flex items-center justify-between text-sm text-gray-600 mb-2">
        <span>Étape {stepInfo.step + 1} sur {stepInfo.totalSteps}</span>
        <span>{Math.round(stepInfo.progress)}%</span>
      </div>
      
      <div className="w-full bg-gray-200 rounded-full h-2 mb-3">
        <div 
          className="bg-blue-600 h-2 rounded-full transition-all duration-500"
          style={{ width: `${stepInfo.progress}%` }}
          role="progressbar"
          aria-valuenow={stepInfo.progress}
          aria-valuemin="0"
          aria-valuemax="100"
          aria-label={`Progression: ${Math.round(stepInfo.progress)}%`}
        />
      </div>
      
      {/* Current step */}
      <div className="text-center">
        <div className="font-medium text-gray-900">{stepInfo.label}</div>
        <div className="text-xs text-gray-500">{stepInfo.flow}</div>
      </div>
    </div>
  );
};

// Feedback Message Component
export const NavigationFeedback = ({ className = '' }) => {
  const { feedbackMessage } = useNavigation();
  
  if (!feedbackMessage || !feedbackMessage.message) return null;
  
  const { type, message } = feedbackMessage;
  
  const typeConfig = {
    success: { icon: CheckCircle, bgColor: 'bg-green-50', textColor: 'text-green-800', borderColor: 'border-green-400' },
    error: { icon: AlertCircle, bgColor: 'bg-red-50', textColor: 'text-red-800', borderColor: 'border-red-400' },
    warning: { icon: AlertCircle, bgColor: 'bg-orange-50', textColor: 'text-orange-800', borderColor: 'border-orange-400' },
    info: { icon: Info, bgColor: 'bg-blue-50', textColor: 'text-blue-800', borderColor: 'border-blue-400' }
  };
  
  const config = typeConfig[type] || typeConfig.info;
  const Icon = config.icon;
  
  return (
    <div 
      className={`fixed top-4 left-1/2 transform -translate-x-1/2 z-50 max-w-sm mx-auto animate-in slide-in-from-top-2 duration-300 ${className}`}
      role="alert"
      aria-live="polite"
    >
      <div className={`p-4 rounded-lg border-l-4 shadow-lg ${config.bgColor} ${config.textColor} ${config.borderColor}`}>
        <div className="flex items-center gap-3">
          <Icon className="h-5 w-5 flex-shrink-0" aria-hidden="true" />
          <div className="text-sm font-medium">
            {message}
          </div>
        </div>
      </div>
    </div>
  );
};

// Complete Navigation Bar for multi-step processes
export const SmartNavigationBar = ({ 
  onCustomBack,
  onCustomNext, 
  nextLabel,
  showProgress = true,
  className = '',
  position = 'bottom' // 'top', 'bottom', 'floating'
}) => {
  const { getCurrentStepInfo } = useNavigation();
  const stepInfo = getCurrentStepInfo();
  
  const positionClasses = {
    top: 'sticky top-0 z-40 bg-white border-b border-gray-200 shadow-sm',
    bottom: 'sticky bottom-0 z-40 bg-white border-t border-gray-200 shadow-sm',
    floating: 'fixed bottom-4 left-1/2 transform -translate-x-1/2 z-50 bg-white rounded-lg shadow-xl border border-gray-200'
  };
  
  return (
    <nav 
      className={`${positionClasses[position]} px-4 py-3 ${className}`}
      role="navigation" 
      aria-label="Navigation du processus en plusieurs étapes"
    >
      <div className="flex items-center justify-between max-w-4xl mx-auto">
        {/* Back Button */}
        <SmartBackButton 
          onCustomBack={onCustomBack}
          variant="minimal"
          disabled={stepInfo?.isFirst && !onCustomBack}
        />
        
        {/* Progress (if enabled) */}
        {showProgress && stepInfo && (
          <div className="flex-1 mx-4">
            <NavigationProgress />
          </div>
        )}
        
        {/* Next Button */}
        <SmartNextButton 
          onCustomNext={onCustomNext}
          label={nextLabel}
          variant={stepInfo?.isLast ? 'success' : 'primary'}
        />
      </div>
    </nav>
  );
};

export default {
  NavigationProvider,
  useNavigation,
  SmartBackButton,
  SmartNextButton, 
  NavigationProgress,
  NavigationFeedback,
  SmartNavigationBar
};